import PropTypes from 'prop-types';
/**
 * Cache an item in local storage
 * @param {string} keyName The name of the item to cache
 * @param {any} data The data to cache
 */
export const cache = (keyName, data) => {
  localStorage.setItem(
    keyName,
    Array.isArray(data) || typeof data === 'object' ? JSON.stringify(data) : data
  );
};
cache.propTypes = {
  keyName: PropTypes.string.isRequired,
  data: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
    PropTypes.array,
  ]).isRequired,
};
/**
 * Cache multiple items in local storage
 * @param {object} dataObj An object containing key-value pairs to cache
 */
export const cacheAll = (dataObj) => {
  Object.keys(dataObj).forEach((key) => {
    cache(key, dataObj[key]);
  });
};
cacheAll.propTypes = {
  dataObj: PropTypes.object.isRequired,
};
/**
 * Fetch a cached item from local storage
 * @param {string} keyName The name of the item to fetch
 * @param {bool} parse Whether to parse the item as JSON
 * @param {any} defaultValue The value to return if the item is not found or errors
 * @returns The value you are looking for, or the default value
 */
export const cacheGet = (keyName, parse = true, defaultValue = null) => {
  const cachedData = localStorage.getItem(keyName);
  if (!cachedData) return defaultValue;
  try {
    return parse ? JSON.parse(cachedData) : cachedData;
  } catch (e) {
    cacheRemove(keyName);
    return defaultValue;
  }
};
cacheGet.propTypes = {
  keyName: PropTypes.string.isRequired,
  parse: PropTypes.bool,
  defaultValue: PropTypes.any,
};
/**
 * Remove an item from local storage
 * @param {string} keyName The name of the item to remove
 * @returns The removed item, or null if it didn't exist
 */
export const cacheRemove = (keyName) => {
  localStorage.removeItem(keyName);
  return cacheGet(keyName, false);
};

cacheRemove.propTypes = {
  keyName: PropTypes.string.isRequired,
};
/**
 * Clear items from local storage
 * @param {array} keyNameList A list of key names to remove from cache. If empty, all keys will be removed.
 * @returns {array} An array of all key names that were removed
 */
export const cacheClear = (keyNameList = []) => {
  let removed = [];
  if (keyNameList.length > 0) {
    keyNameList.forEach((keyName) => {
      cacheRemove(keyName);
      removed.push(keyName);
    });
  } else {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      removed.push(key);
    }
    localStorage.clear();
  }
  return removed;
};

cacheClear.propTypes = {
  keyNameList: PropTypes.array,
};
